<template lang="pug">
.curso-main-container.pb-3
  BannerInterno
  .container.tarjeta.tarjeta--blanca.p-4.p-md-5.mb-5
    .titulo-principal.color-acento-contenido
      .titulo-principal__numero
        span 2
      h1 Creatividad: obstáculos y bloqueos 
    
    .row.justify-content-center.align-items-center.mb-5
      .col-lg-4.col-md-6.col-7.mb-3.mb-lg-0(data-aos="fade-right")
        img(src='@/assets/curso/temas/t2/1.png', alt='')
      .col-lg-8(data-aos="fade-left")
        p Los factores que afectan la creatividad en la vida diaria no aparecen de manera aislada. Con el tiempo, y según el entorno en el que se desenvuelve la persona, estos factores pueden consolidarse en bloqueos más profundos que limitan de forma sistemática la capacidad de pensar de manera diferente y proponer soluciones innovadoras en los diferentes espacios de interacción del ser humano.
        p Mientras que los factores se manifiestan como hábitos, creencias o actitudes individuales, los obstáculos y bloqueos de la creatividad actúan como barreras que condicionan:
        .row.justify-content-center.mb-4
          .col-lg-4.col-md-6.mb-3.mb-lg-0
            .tag-shape.tag-shape_1-1.h-100.p-4
              p.text-center.mb-0 La percepción de los problemas.
          .col-lg-4.col-md-6.mb-3.mb-lg-0.px-lg-0
            .tag-shape.tag-shape_1-2.h-100.p-4
              p.text-center.mb-0 La toma de decisiones.
          .col-lg-4.col-md-6.mb-3.mb-lg-0
            .tag-shape2.tag-shape2_1-3.h-100.p-4
              p.text-center.mb-0 La disposición al cambio.
        p Estos no solo dependen de la persona, sino también de la cultura organizacional y de las dinámicas sociales en las que participa.

    .mb-5(data-aos="fade")
      .titulo-sexto.color-acento-contenido.mb-3
        h5 Tabla 1.
        span Tipos de los bloqueos de la creatividad
      .tabla-b.color-primario
        table
          tbody
            tr
              td.fw-bold Bloqueos perceptuales
              td Se presentan cuando existe dificultad para identificar con claridad una situación problemática o para establecer relaciones entre sus elementos. En estos casos, la persona puede centrarse únicamente en lo evidente, pasar por alto información relevante o definir de manera incorrecta el problema, lo que limita la generación de alternativas de solución.
            tr
              td.fw-bold Bloqueos culturales
              td Están asociados a los procesos de socialización, la educación recibida y las normas implícitas del entorno. Creencias arraigadas, costumbres, tradiciones y modelos educativos rígidos pueden restringir la curiosidad, la imaginación y la disposición a cuestionar lo establecido.
            tr
              td.fw-bold Bloqueos emocionales
              td Tienen su origen en estados internos como el miedo al error, la inseguridad, la baja autoestima o el temor al juicio de los demás. Estos bloqueos influyen directamente en la forma como la persona se expresa, participa y propone ideas, afectando su confianza para asumir riesgos creativos.
            tr
              td.fw-bold Bloqueos organizacionales
              td Surgen en entornos laborales donde existe una alta presión por resultados, sanción al error, exceso de control o falta de estímulos para la innovación. En estos contextos, aunque la persona tenga disposición creativa, el ambiente puede inhibir la iniciativa y la experimentación.

    .bg3.p-4
      .row.justify-content-center.align-items-center
        .col-lg-1.col-md-2.col-3
          img(src='@/assets/curso/temas/t2/3.svg', alt='')
        .col-lg-11.col-md-10.col-9
          p.mb-0 Reconocer la diferencia entre #[b factores individuales y bloqueos estructurales] permite comprender por qué la creatividad no siempre se manifiesta de manera espontánea. Este reconocimiento constituye un paso fundamental para transformar la forma de pensar, asumir los conflictos laborales con una mirada más amplia y avanzar hacia una gestión creativa y responsable de las situaciones organizacionales.


    Separador
    #t_2_1.titulo-segundo.color-acento-contenido(data-aos='fade-right')
      h2 2.1 Técnicas para desarrollar la creatividad

    p Dado que la creatividad es una habilidad que se ejercita, existen diversas técnicas que permiten estimularla y aplicarla a la solución de problemas. La elección de cada técnica depende del tipo de situación, del contexto y de si se trabaja de manera individual o grupal.
    p.mb-4 Entre las principales técnicas se destacan:

    .row.justify-content-center.mb-5
      .col-lg-8(data-aos="fade-right")
        AcordionA(tipo="b" clase-tarjeta="tarjeta bg8")
          .row(titulo="<em>Hyperthinking</em> (hiperpensamiento)")
            .col-md-11
              p.mb-0 Promueve la adaptación al cambio, el aprendizaje continuo, la conexión con redes de información y la acción transformadora.
          .row(titulo="Pensamiento lateral")
            .col-md-11
              p.mb-0 Orientado a romper esquemas tradicionales y generar ideas innovadoras mediante provocaciones y cambios de perspectiva.
          .row(titulo="Seis sombreros para pensar")
            .col-md-11
              p.mb-0 Facilita el análisis de una situación desde distintos enfoques de manera ordenada y constructiva.
          .row(titulo="Matriz DOFA")
            .col-md-11
              p.mb-0 Útil para identificar fortalezas, debilidades, oportunidades y amenazas, y formular estrategias coherentes.
          .row(titulo="Mapas mentales")
            .col-md-11
              p.mb-0 Permiten organizar ideas de forma visual y asociativa.
          .row(titulo="SCAMPER")
            .col-md-11
              p.mb-0 Técnica basada en preguntas que invitan a modificar, adaptar o reinventar productos, servicios o procesos.  (Sustituir, Combinar, Adaptar, Modificar, Poner en otros usos, Eliminar y Reorganizar).
          .row(titulo="<em>Brainstorming</em> y <em>brainwriting</em>")
            .col-md-11
              p.mb-0 Métodos grupales para la generación de ideas sin juicios previos.
          .row(titulo="Juegos creativos")
            .col-md-11
              p.mb-0 Estudios de caso y juegos de roles, que favorecen la exploración, la imaginación y la toma de decisiones en contextos simulados.
      .col-lg-4.d-none.d-lg-block(data-aos="fade-left")
        img(src='@/assets/curso/temas/t2/4.png', alt='')  

    p Estas técnicas facilitan el análisis creativo de los conflictos y fortalecen la capacidad de proponer soluciones alineadas con los objetivos organizacionales.
    p Para complementar la información, lo invitamos a visitar el siguiente video.

    .row.bgc-1.align-items-center.p-2.p-md-3.pb-5(data-aos="fade-down")
      .col-lg-12.col-12.px-lg-4
        .row.justify-content-center.align-items-center
         
          .row.justify-content-center
            .col-12
              figure
                .video
                  iframe(width="560" height="315" src="https://www.youtube.com/embed/fx0opRswqa8?si=Sb3tHP-FQgyPc11q" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen)
              figcaption Video. Preguntas de reflexión


    Separador
    #t_2_2.titulo-segundo.color-acento-contenido(data-aos='fade-right')
      h2 2.2 El pensamiento como base del comportamiento creativo

    .row.justify-content-center.align-items-center.mb-4
      .col-lg-5.col-md-9.mb-3.mb-lg-0(data-aos="fade-right")
        img(src='@/assets/curso/temas/t2/5.png', alt='')
      .col-lg-7(data-aos="fade-left")
        p La forma en que una persona piensa determina la manera en que interpreta las situaciones, enfrenta los problemas y construye posibles soluciones. El pensamiento no actúa de manera aislada: está estrechamente relacionado con las emociones, las creencias y las experiencias previas. Por esta razón, estados emocionales como #[b el miedo, la inseguridad o la falta de confianza pueden restringir la creatividad, mientras que una actitud abierta, flexible y positiva favorece la exploración de ideas y la innovación].
        .row.justify-content-center.align-items-center.mb-4
          .col-lg-2.col-md-2.col-3
            img(src='@/assets/curso/temas/t2/6.svg', alt='')
          .col-lg-10.col-md-10.col-9
            p.mb-0 El pensamiento creativo se manifiesta cuando se cuestionan las respuestas automáticas, se analizan los problemas desde distintas perspectivas y se evita reaccionar de manera impulsiva ante la dificultad. Esto implica reconocer que no siempre existe una única forma correcta de actuar y que los errores hacen parte natural del proceso de aprendizaje y mejora.

    .row.justify-content-center(data-aos="fade-down")
      .col-lg-4.mb-3.mb-lg-0
        .tarjeta.bg1.p-4.h-100.d-flex.align-items-center
          p.mb-0 Desarrollar el pensamiento creativo supone, entonces, aprender a gestionar el error de manera constructiva, comprender el fracaso como una fuente de información y adoptar una postura reflexiva frente a las dificultades. Muchas soluciones innovadoras han surgido de intentos fallidos, ajustes progresivos o decisiones que inicialmente no dieron el resultado esperado. 
      .col-lg-3.d-none.d-lg-block
        img(src='@/assets/curso/temas/t2/7.svg', alt='')          
      .col-lg-5
        .cajon.color-secundario.bg3.p-4.mb-4
          p.mb-0 En este sentido, equivocarse no representa un retroceso, sino una oportunidad para replantear, mejorar y fortalecer las ideas.
        p Cuando el pensamiento se orienta desde esta perspectiva, se favorece una conducta más consciente y responsable, capaz de transformar los conflictos en oportunidades de aprendizaje, mejorar la toma de decisiones y contribuir al desarrollo de soluciones creativas alineadas con los objetivos organizacionales.


    Separador
    #t_2_3.titulo-segundo.color-acento-contenido(data-aos='fade-right')
      h2 2.3 Actitudes mentales que fortalecen el pensamiento creativo

    p Para consolidar una creatividad aplicada al entorno laboral, es fundamental fortalecer ciertas actitudes internas, entre ellas:

    .row.justify-content-center.mb-4(data-aos="fade-down")
      .col-lg-3.col-md-6.col-9.mb-3.mb-lg-0
        .tarjeta-avatar
          img(src='@/assets/curso/temas/t2/tarj1-1.svg', alt='')
          .tarjeta.bg2.w-100.text-center
            .p-4
              p.mb-0 Aprender de los fracasos, reconociéndolos como oportunidades de mejora y no como obstáculos definitivos.
      .col-lg-3.col-md-6.col-9.mb-3.mb-lg-0
        .tarjeta-avatar
          img(src='@/assets/curso/temas/t2/tarj1-2.svg', alt='')
          .tarjeta.bg2.w-100.text-center
            .p-4
              p.mb-0 Fortalecer la autoestima, confiando en las propias ideas y capacidades.
      .col-lg-3.col-md-6.col-9.mb-3.mb-lg-0
        .tarjeta-avatar
          img(src='@/assets/curso/temas/t2/tarj1-3.svg', alt='')
          .tarjeta.bg2.w-100.text-center
            .p-4
              p.mb-0 Manejar la crítica de manera constructiva, escuchando, reflexionando y ajustando cuando sea necesario.
      .col-lg-3.col-md-6.col-9
        .tarjeta-avatar
          img(src='@/assets/curso/temas/t2/tarj1-4.svg', alt='')
          .tarjeta.bg2.w-100.text-center
            .p-4
              p.mb-0 Aceptar diferentes puntos de vista, comprendiendo que cada persona interpreta la realidad desde su experiencia y contexto.

    .row.justify-content-center.align-items-center.mb-4
      .col-lg-1.col-md-2.col-3
        img(src='@/assets/curso/temas/t2/8.svg', alt='')
      .col-lg-11.col-md-10.col-9
        p.mb-0 En esta primera parte ha reconocido que la creatividad y la forma de pensar influyen directamente en la manera de afrontar los conflictos laborales. Comprender que los errores, las emociones y las creencias hacen parte del proceso permite asumir los desafíos con una mirada más abierta y propositiva.
    
    p Este recorrido inicial invita a cuestionar lo establecido, fortalecer el pensamiento creativo y prepararlo para aplicar nuevas estrategias en situaciones reales del entorno organizacional.


</template>

<script>
export default {
  name: 'Tema2',
  data: () => ({
    // variables de vue
  }),
  mounted() {
    this.$nextTick(() => {
      this.$aosRefresh()
    })
  },
  updated() {
    this.$aosRefresh()
  },
}
</script>

<style lang="sass"></style>
